import { yupResolver } from '@hookform/resolvers/yup';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { getInitialFormState } from '../../constants.ts';
import type { LocationState, OrganizationFormData } from '../../types.ts';
import { useOrganizationContext } from '../../utils/context/OrganizationContext.tsx';
import { OpenAudienceAccessSection } from './sections/OpenAudienceAccessSection.tsx';
import { OrganizationDetailsSections } from './sections/OrganizationDetailsSection.tsx';
import { transformCreateFormDataToApiPayload } from './utils.ts';
import { getValidationSchema } from './validationSchema/validationSchema.ts';

export const OrganizationCreateForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { useCreateOrganization } = useOrganizationContext();
  const { createOrganization, loading } = useCreateOrganization();
  const { open, close, setLoading } = useConfirmationDialog();
  const { state }: { state: LocationState | null } = useLocation();
  const selectedParentId = state?.selectedParentId;

  const handleOpenDialog = useCallback(
    (data: OrganizationFormData) => {
      open({
        cancelLabel: t('Cancel'),
        confirmationButtonColor: 'primary',
        confirmationLabel: t('Create Organization'),
        content: (
          <>
            <DialogContent dividers sx={{ mb: 6 }}>
              <Typography>
                {t('Please make sure all organization details are correct.')}{' '}
                <Typography variant="body1Bold" component={'span'}>
                  {t(
                    'Once created, this organization cannot be moved within the hierarchy or deleted. Its name and details can be edited.'
                  )}
                </Typography>
              </Typography>
            </DialogContent>
            <Divider />
          </>
        ),
        customContent: true,
        onConfirmed: async () => {
          const payload = transformCreateFormDataToApiPayload(data);
          setLoading(true);

          try {
            await createOrganization(payload);
          } finally {
            setLoading(false);
            close();
          }
        },
        title: t('Are you sure you want to create this organization?'),
      });
    },
    [close, createOrganization, open, setLoading, t]
  );

  const initialValues = getInitialFormState(selectedParentId);

  const formMethods = useForm<OrganizationFormData>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const onSubmit = (data: OrganizationFormData) => {
    handleOpenDialog(data);
  };

  return (
    <PageHeader title={t('Create new Organization')} titlePrefix="OpenXSelect">
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit, scrollToError)}>
          <FormContainer fullPage>
            <OrganizationDetailsSections initialFormValues={initialValues} />
            <OpenAudienceAccessSection isEdit={false} />

            <FormActions
              submitButtonLabel={t('Save')}
              cancelButtonLabel={t('Cancel')}
              onCancel={() => navigate('/organizations')}
              isSubmitting={loading}
            />
          </FormContainer>
        </form>
      </FormProvider>
    </PageHeader>
  );
};
